/**
 * AgentTaskCard - Compact card for grid view with live tool-use updates
 * Polls task status and shows progress
 */

import React, { useEffect, useState } from "react";
import "./AgentTaskCard.css";

const getApiBase = () => {
    if (process.env.NODE_ENV === "production") {
        return process.env.REACT_APP_BACKEND_URL || "";
    }
    return process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
};

export interface AgentTaskCardProps {
    taskId: string;
    title: string;
    sessionId?: string;
    darkMode?: boolean;
    onClick?: () => void;
}

interface LogEntry {
    type: string;
    tool_name?: string;
    tool_input?: Record<string, unknown>;
}

interface TaskData {
    id: string;
    title: string;
    status: string;
    result_summary?: string;
    logs?: LogEntry[];
}

function getLastToolCalls(logs: LogEntry[]): string[] {
    const names: string[] = [];
    for (let i = logs.length - 1; i >= 0 && names.length < 3; i--) {
        const log = logs[i];
        if (log.type === "tool_call" && log.tool_name && !names.includes(log.tool_name)) {
            names.unshift(log.tool_name);
        }
    }
    return names;
}

export const AgentTaskCard: React.FC<AgentTaskCardProps> = ({
    taskId,
    title,
    darkMode = false,
    onClick,
}) => {
    const [task, setTask] = useState<TaskData | null>(null);

    useEffect(() => {
        if (!taskId) return;

        const poll = async () => {
            try {
                const res = await fetch(
                    `${getApiBase()}/api/agent/tasks/${encodeURIComponent(taskId)}?include=logs`
                );
                if (!res.ok) return;
                const data = await res.json();
                setTask(data);
            } catch {
                // ignore
            }
        };

        poll();
        const interval = setInterval(poll, 2500);
        return () => clearInterval(interval);
    }, [taskId]);

    const status = task?.status || "todo";
    const lastTools = task?.logs ? getLastToolCalls(task.logs) : [];

    return (
        <div
            className={`agent-task-card ${darkMode ? "dark" : ""} status-${status}`}
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onClick?.();
                }
            }}
        >
            <div className="agent-task-card-header">
                <span className="agent-task-card-title">{title}</span>
                <span className={`agent-task-card-status status-${status}`}>{status.replace("_", " ")}</span>
            </div>
            {lastTools.length > 0 && (
                <div className="agent-task-card-tools">
                    <span className="agent-task-card-tools-label">Tools used:</span>
                    <span className="agent-task-card-tools-list">
                        {lastTools.map((t) => t.replace(/_/g, " ")).join(" → ")}
                    </span>
                </div>
            )}
            {task?.result_summary && (
                <p className="agent-task-card-preview">
                    {task.result_summary.slice(0, 120)}
                    {task.result_summary.length > 120 ? "…" : ""}
                </p>
            )}
        </div>
    );
};
